<template>
    <b-modal
        id="m-kelola-peserta"
        title="Kelola Peserta"
        centered
        hide-footer
        @shown="initChoices"
    >
        <div v-if="submission" class="p-1">
            <p
                v-if="requireDownPayment && submission.payment_status !== PAYMENT_STATUS.PAID"
                class="text-danger font-weight-bold small mb-3"
            >
                * Down Payment wajib DIBAYAR terlebih dahulu.
            </p>

            <div
                v-if="isWaitlisted"
                class="text-center p-3 text-muted font-weight-bold"
            >
                Peserta masih dalam waitlist.
            </div>

            <div
                v-else-if="allDone"
                class="text-center p-3 text-muted font-weight-bold"
            >
                Semua slot sudah selesai ditandai.
            </div>

            <template v-else>
                <p class="text-muted small mb-3">
                    <template v-if="attendancePhase">
                        Tahap 1 — tandai <b>kehadiran</b> tiap slot dulu.
                        Pencapaian target muncul setelah kehadiran disimpan.
                    </template>
                    <template v-else>
                        Tahap 2 — tandai <b>pencapaian target</b> untuk slot yang hadir.
                    </template>
                </p>

                <b-row>
                    <b-col
                        v-for="(slot, index) in slots"
                        :key="slot.id"
                        cols="12"
                        class="mb-3"
                    >
                        <div class="border rounded p-2 h-100">
                            <div class="font-weight-bold mb-2">
                                Slot {{ index + 1 }}
                            </div>

                            <!-- Kehadiran -->
                            <div class="mb-2">
                                <label class="small font-weight-bold mb-1 d-block">
                                    Kehadiran
                                </label>

                                <b-badge
                                    v-if="slot.attendance_status !== null"
                                    :variant="getAttendanceStatusBadge(slot.attendance_status)[0]"
                                >
                                    {{ getAttendanceStatusBadge(slot.attendance_status)[1] }}
                                </b-badge>

                                <div v-else-if="attendancePhase" class="d-flex">
                                    <b-button
                                        size="sm"
                                        class="mr-2 flex-fill"
                                        :variant="choices[slot.id].attendance === ATTENDANCE_STATUS.PRESENT ? 'success' : 'outline-success'"
                                        :disabled="isSubmitting || attendanceLocked"
                                        @click="setAttendance(slot.id, ATTENDANCE_STATUS.PRESENT)"
                                    >
                                        <i class="fa fa-check mr-1" /> Hadir
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        class="flex-fill"
                                        :variant="choices[slot.id].attendance === ATTENDANCE_STATUS.ABSENT ? 'danger' : 'outline-danger'"
                                        :disabled="isSubmitting || attendanceLocked"
                                        @click="setAttendance(slot.id, ATTENDANCE_STATUS.ABSENT)"
                                    >
                                        <i class="fa fa-times mr-1" /> Absen
                                    </b-button>
                                </div>

                                <span v-else class="text-muted small">Belum ditandai</span>
                            </div>

                            <!-- Pencapaian Target: hanya slot Hadir, di tahap 2 -->
                            <div v-if="slot.attendance_status === ATTENDANCE_STATUS.PRESENT">
                                <label class="small font-weight-bold mb-1 d-block">
                                    Pencapaian Target
                                </label>

                                <b-badge
                                    v-if="slot.achievement_status !== null"
                                    :variant="getAchievementStatusBadge(slot.achievement_status)[0]"
                                >
                                    {{ getAchievementStatusBadge(slot.achievement_status)[1] }}
                                </b-badge>

                                <div v-else-if="!attendancePhase" class="d-flex">
                                    <b-button
                                        size="sm"
                                        class="mr-2 flex-fill"
                                        :variant="choices[slot.id].achievement === ACHIEVEMENT_STATUS.ACHIEVED ? 'success' : 'outline-success'"
                                        :disabled="isSubmitting"
                                        @click="choices[slot.id].achievement = ACHIEVEMENT_STATUS.ACHIEVED"
                                    >
                                        <i class="fa fa-trophy mr-1" /> Tercapai
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        class="flex-fill"
                                        :variant="choices[slot.id].achievement === ACHIEVEMENT_STATUS.NOT_ACHIEVED ? 'danger' : 'outline-danger'"
                                        :disabled="isSubmitting"
                                        @click="choices[slot.id].achievement = ACHIEVEMENT_STATUS.NOT_ACHIEVED"
                                    >
                                        <i class="fa fa-ban mr-1" /> Tidak Tercapai
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </b-col>
                </b-row>

                <b-button
                    variant="primary"
                    block
                    :disabled="isSubmitting"
                    @click="save"
                >
                    <b-spinner v-if="isSubmitting" small class="mr-1" />
                    {{ attendancePhase ? "Simpan Kehadiran" : "Simpan Target" }}
                </b-button>
            </template>
        </div>
    </b-modal>
</template>

<script>
import {
    SUBMISSION_STATUS,
    PAYMENT_STATUS,
    ATTENDANCE_STATUS,
    ACHIEVEMENT_STATUS,
    getAttendanceStatusBadge,
    getAchievementStatusBadge
} from "../konserHelper";

export default {
    name: "ModalTindakan",
    props: {
        submission: {
            type: Object,
            required: true
        },
        requireDownPayment: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            SUBMISSION_STATUS,
            PAYMENT_STATUS,
            ATTENDANCE_STATUS,
            ACHIEVEMENT_STATUS,
            isSubmitting: false,
            choices: {}
        };
    },
    watch: {
        submission: {
            immediate: true,
            handler() {
                this.initChoices();
            }
        }
    },
    computed: {
        slots() {
            return (this.submission && this.submission.slots) || [];
        },
        isWaitlisted() {
            return (
                this.submission &&
                this.submission.status === this.SUBMISSION_STATUS.WAITLISTED
            );
        },
        // Fase 1 (kehadiran) selama masih ada slot yang belum ditandai hadir.
        attendancePhase() {
            return this.slots.some(s => s.attendance_status === null);
        },
        attendanceLocked() {
            return (
                this.requireDownPayment &&
                this.submission.payment_status !== this.PAYMENT_STATUS.PAID
            );
        },
        allDone() {
            return this.slots.length > 0 && this.slots.every(s => this.slotDone(s));
        }
    },
    methods: {
        getAttendanceStatusBadge,
        getAchievementStatusBadge,

        initChoices() {
            const choices = {};
            this.slots.forEach(slot => {
                choices[slot.id] = {
                    attendance: slot.attendance_status,
                    achievement: slot.achievement_status
                };
            });
            this.choices = choices;
        },

        slotDone(slot) {
            return (
                slot.attendance_status !== null &&
                (slot.achievement_status !== null ||
                    slot.attendance_status === this.ATTENDANCE_STATUS.ABSENT)
            );
        },

        setAttendance(slotId, value) {
            this.choices[slotId].attendance = value;
            if (value === this.ATTENDANCE_STATUS.ABSENT) {
                this.choices[slotId].achievement = null;
            }
        },

        buildPayload() {
            const payload = [];
            this.slots.forEach(slot => {
                const c = this.choices[slot.id];
                if (!c) return;

                const entry = { slot_id: slot.id };
                let has = false;

                if (slot.attendance_status === null && c.attendance !== null) {
                    entry.attendance_status = c.attendance;
                    has = true;
                }

                if (
                    slot.attendance_status === this.ATTENDANCE_STATUS.PRESENT &&
                    slot.achievement_status === null &&
                    c.achievement !== null
                ) {
                    entry.achievement_status = c.achievement;
                    has = true;
                }

                if (has) payload.push(entry);
            });
            return payload;
        },

        save() {
            const slots = this.buildPayload();

            if (!slots.length) {
                this.$helper.toastError(this, "Tidak ada perubahan untuk disimpan.");
                return;
            }

            this.isSubmitting = true;
            this.$axios
                .post(`concerts/submissions/${this.submission.id}/slots/outcomes`, {
                    slots
                })
                .then(() => {
                    this.$helper.toastSuccess(this, "Peserta berhasil diperbarui!");
                    this.$emit("refresh");
                    // Tetap buka: setelah kehadiran tersimpan, modal lanjut ke tahap target.
                    if (this.allDone) {
                        this.$bvModal.hide("m-kelola-peserta");
                    }
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false));
        }
    }
};
</script>
